import * as React from 'react';
import { PluginKey } from '@tiptap/pm/state';
import { gitHubEmojis, type EmojiItem } from '@tiptap/extension-emoji';
import { getElementOverflowPosition } from '@cotera/client/app/components/ui/tiptap/utils';
import {
  SuggestionMenu,
  type SuggestionItem,
  type SuggestionMenuRenderProps,
} from '@cotera/client/app/components/ui/tiptap/tiptap-ui-utils/suggestion-menu';
import { Card, CardContent } from '@cotera/client/v0/components/ui/card';
import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';

export const EMOJI_DROPDOWN_PLUGIN_KEY = new PluginKey('emojiSuggestionMenu');

const MAX_RESULTS = 50;

const matchesQuery = (emoji: EmojiItem, query: string): boolean => {
  if (emoji.name.includes(query)) {
    return true;
  }
  if (emoji.shortcodes?.some((code) => code.includes(query)) === true) {
    return true;
  }
  if (emoji.tags?.some((tag) => tag.includes(query)) === true) {
    return true;
  }
  return false;
};

const filterEmojis = (query: string): EmojiItem[] => {
  const normalized = query.trim().toLowerCase();
  if (normalized === '') {
    return gitHubEmojis.slice(0, MAX_RESULTS);
  }
  const matches: EmojiItem[] = [];
  for (const emoji of gitHubEmojis) {
    if (matchesQuery(emoji, normalized)) {
      matches.push(emoji);
      if (matches.length >= MAX_RESULTS) {
        break;
      }
    }
  }
  return matches;
};

const buildItems = (query: string): SuggestionItem<EmojiItem>[] =>
  filterEmojis(query).map((emoji) => ({
    title: emoji.name,
    subtext: emoji.shortcodes?.[0] ?? emoji.name,
    context: emoji,
    onSelect: ({ editor }) => {
      editor.chain().focus().setEmoji(emoji.name).run();
    },
  }));

const SELECTOR = 'emoji-suggestion-menu';

const Item = ({
  item,
  isSelected,
  onSelect,
}: {
  item: SuggestionItem<EmojiItem>;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const itemRef = React.useRef<HTMLButtonElement>(null);
  const emoji = item.context;

  React.useEffect(() => {
    const container = document.querySelector(
      `[data-selector="${SELECTOR}"]`
    ) as HTMLElement | null;
    if (itemRef.current === null || !isSelected || container === null) {
      return;
    }
    const overflow = getElementOverflowPosition(itemRef.current, container);
    if (overflow === 'top') {
      itemRef.current.scrollIntoView(true);
    } else if (overflow === 'bottom') {
      itemRef.current.scrollIntoView(false);
    }
  }, [isSelected]);

  const display = emoji?.emoji;
  const fallback = emoji?.fallbackImage;

  return (
    <Button
      ref={itemRef}
      type="button"
      variant="ghost"
      className={cn(
        'tiptap-button !justify-start !bg-transparent hover:!bg-gray-100',
        'data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground'
      )}
      data-active-state={isSelected ? 'on' : 'off'}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 w-full">
        <span className="w-5 text-base leading-none flex items-center justify-center flex-shrink-0">
          {display !== undefined ? (
            display
          ) : fallback !== undefined ? (
            <img src={fallback} alt={item.title} className="w-4 h-4" />
          ) : (
            ':'
          )}
        </span>
        <span className="tiptap-button-text text-left text-sm">
          :{item.title}:
        </span>
      </div>
    </Button>
  );
};

const List = ({
  items,
  selectedIndex,
  onSelect,
}: SuggestionMenuRenderProps<EmojiItem>) => {
  if (items.length === 0) {
    return (
      <Card className="!py-2">
        <CardContent className="w-[240px] !px-2 py-3 text-xs text-muted-foreground">
          No emoji found
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="!py-2">
      <CardContent className="w-[240px] !px-2 flex flex-col gap-0.5 overflow-y-auto max-h-[320px]">
        {items.map((item, index) => (
          <Item
            key={`emoji-${item.title}`}
            item={item}
            isSelected={index === selectedIndex}
            onSelect={() => onSelect(item)}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export const EmojiSuggestionMenu = () => {
  return (
    <SuggestionMenu
      char=":"
      pluginKey={EMOJI_DROPDOWN_PLUGIN_KEY}
      selector={SELECTOR}
      items={({ query }) => buildItems(query)}
    >
      {(props) => <List {...props} />}
    </SuggestionMenu>
  );
};
